import React, { type FC } from "react";
import {
    type FormatMonthlyPriceType,
    CommonMonthlyPriceLabelKey,
} from "../../../../../../../../common-deprecated/priceUtils";
import { AemFmComponent } from "../../../../../../../../common-deprecated/utils/aemFlexibilityMatrixUtils";
import { useGradeSelectorSelector } from "../../../../../redux/hooks";
import {
    getAemMonthlyRateFormat,
    showAemMonthlyRates,
} from "../../../../../../../../common-deprecated/settings/utils/financeSettingUtilsAem";
import Button from "../../../../../../shared-components/css-modules/toyota/atoms/Button";
import Icon from "../../../../../../shared-components/css-modules/toyota/atoms/Icon";
import type {
    ApiGradeBodyEngineTransmissionWheeldriveCarType,
    ApiPriceType,
    SBRockarCarType,
} from "../../../../../../../../common-deprecated/types/ApiModel";
import { formatMonthlyFinancePrice, mapPrice } from "../../../../../../shared-logic/utils/priceUtils";
import { RequestStatusEnum } from "../../../../../../../../common-deprecated/types/CommonTypes";
import styles from "./Price.module.scss";
import { MODAL_DISCLAIMER } from "../../../../../utils/modalConstants";
import useGradeSelectorModal from "../../../../../hooks/useGradeSelectorModal";

const MonthlyPrice: FC<{
    car: ApiGradeBodyEngineTransmissionWheeldriveCarType | SBRockarCarType;
    selectedColourId?: string;
    price: ApiPriceType;
}> = ({ car, selectedColourId, price }) => {
    const commonSettings = useGradeSelectorSelector((state) => state.commonSettings);
    const modelCode = useGradeSelectorSelector((state) => state.gradeSelectorModel.model.code);
    const monthlyEnabled = showAemMonthlyRates(commonSettings, AemFmComponent.GradeSelector, "new", modelCode);
    const { state, cars } = useGradeSelectorSelector((gss) => gss.gradeSelectorFinance);
    const financeId = `${(car as ApiGradeBodyEngineTransmissionWheeldriveCarType).CarID || (car as SBRockarCarType).vehicle.base.externalId}-${selectedColourId as string}`;
    const { showModalHelper } = useGradeSelectorModal();

    if (!monthlyEnabled) {
        return null;
    }

    if (state === RequestStatusEnum.Fetching) {
        return <span className={styles.loadbar} style={{ width: "100px", height: "20px" }} />;
    }

    const carFinance = cars[financeId];

    if (!carFinance) {
        return null;
    }

    const { financeInfo, quote } = carFinance;
    const monthlyPriceFormat = getAemMonthlyRateFormat(commonSettings, AemFmComponent.GradeSelector, "new", modelCode);
    const { labels = [], secondaryLabels = [] } = financeInfo
        ? formatMonthlyFinancePrice<FormatMonthlyPriceType>(
              mapPrice(price, financeInfo),
              financeInfo,
              commonSettings,
              monthlyPriceFormat,
              undefined,
              undefined,
              undefined,
              true,
          )
        : { labels: [] };

    if (labels.length === 0) {
        return null;
    }

    return (
        <span>
            {labels.map(({ key, value }) => (
                <span key={key}>
                    {(key === CommonMonthlyPriceLabelKey.Price || key === CommonMonthlyPriceLabelKey.DownPayment) && (
                        <strong>
                            <span dangerouslySetInnerHTML={{ __html: value }} />
                        </strong>
                    )}
                </span>
            ))}

            {secondaryLabels && secondaryLabels.length > 0 && <span> / </span>}

            {secondaryLabels.map(({ key, value }) => (
                <span key={key}>
                    {key === CommonMonthlyPriceLabelKey.Price && <span dangerouslySetInnerHTML={{ __html: value }} />}
                </span>
            ))}

            {!commonSettings.hideFinanceTooltip && quote && (
                <Button
                    variant="clean"
                    type="button"
                    onClick={() =>
                        showModalHelper(MODAL_DISCLAIMER, {
                            content: (
                                <div>
                                    {quote.productName && <strong>{quote.productName}</strong>}
                                    {quote.calculations && quote.calculations.length !== 0 && (
                                        <table>
                                            <tbody>
                                                {quote.calculations.map(({ name, value, format }) => (
                                                    <tr style={{ color: format?.colour }} key={name}>
                                                        <td>
                                                            {format?.b ? <strong>{name}</strong> : <span>{name}</span>}
                                                        </td>
                                                        <td>
                                                            {format?.b ? (
                                                                <strong dangerouslySetInnerHTML={{ __html: value }} />
                                                            ) : (
                                                                <span dangerouslySetInnerHTML={{ __html: value }} />
                                                            )}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    )}
                                    <div dangerouslySetInnerHTML={{ __html: quote.value }} />
                                </div>
                            ),
                        })
                    }
                >
                    <Icon variant="info" sizing="md" aria-hidden="true" />
                </Button>
            )}
        </span>
    );
};

export default MonthlyPrice;
